/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { accessAPI } from "../../utils";
import { useNavigate, useLocation } from "react-router-dom";
import { AutoComplete } from "primereact/autocomplete";

/**
 * Componente Buscador genérico con autocompletado y resultados agrupados.
 *
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.config - Configuración del buscador
 * @param {string} props.config.urlBusquedaRapida - URL para obtener sugerencias (se agrega &query={query})
 * @param {string} props.config.urlBusqueda - URL de navegación al presionar Enter (se agrega /{query} al final)
 * @param {string} [props.config.placeholder="Buscar..."] - Placeholder del input
 * @param {number} [props.config.delay=400] - Delay en ms antes de buscar sugerencias
 * @param {number} [props.config.minLength=2] - Mínimo de caracteres para iniciar búsqueda
 * @param {Object} [props.config.grupos] - Configuración de grupos {key: {etiqueta, campo, urlBase}}
 * @param {Function} [props.onNavegar] - Callback ejecutado al navegar a un resultado (por ejemplo para cerrar el menú móvil)
 */
export default function Buscador({ config = {}, onNavegar }) {
  const {
    urlBusquedaRapida,
    urlBusqueda,
    placeholder = "Buscar...",
    delay = 400,
    minLength = 2
  } = config;

  const [busqueda, setBusqueda] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const [cargando, setCargando] = useState(false);

  const timeoutRef = useRef(null);
  const autoCompleteRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  function buscarSugerencias(event) {
    const query = event.query;

    if (query.length < minLength) {
      setSugerencias([]);
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setCargando(true);

    timeoutRef.current = setTimeout(() => {
      accessAPI(
        "GET",
        `${urlBusquedaRapida}&query=${encodeURIComponent(query)}`,
        null,
        (respuesta) => {
          // const gruposTransformados = transformarRespuesta(respuesta);
          // console.log(gruposTransformados);
          setSugerencias(respuesta);
          setCargando(false);
        },
        () => {
          setSugerencias([]);
          setCargando(false);
        }
      );
    }, delay);
  }

  function handleKeyDown(event) {
    if (event.key === "Enter" && busqueda.trim()) {
      // Si hay una sugerencia resaltada con las flechas, el AutoComplete ya la
      // seleccionó en su propio handler (y onSelect navega): este Enter solo
      // maneja la búsqueda por texto cuando no hay nada resaltado.
      const overlay = autoCompleteRef.current?.getOverlay();
      if (overlay?.querySelector('li[data-p-highlight="true"]')) {
        return;
      }
      event.preventDefault();
      autoCompleteRef.current?.hide();
      setSugerencias([]);
      setBusqueda("");
      event.target.blur();
      const totalItems = sugerencias.reduce((sum, grupo) => sum + (grupo.items?.length || 0), 0);
      if (totalItems === 1) {
        const unicoItem = sugerencias.find((g) => g.items?.length)?.items[0];
        navigate(unicoItem.url);
      } else {
        navigate(`${urlBusqueda}/${encodeURIComponent(busqueda.trim())}?volver=${encodeURIComponent(location.pathname)}`);
      }
      onNavegar?.();
    }
  }

  function handleSeleccion(event) {
    if (event.value?.url) {
      setBusqueda("");
      // Si presiona Cmd/Ctrl, abrir en nueva pestaña
      if (event.originalEvent.metaKey || event.originalEvent.ctrlKey) {
        window.open(event.value.url, "_blank");
      } else {
        // Usar navigate para navegación interna sin recargar
        navigate(event.value.url);
        onNavegar?.();
      }
    }
  }

  return (
    <AutoComplete
      ref={autoCompleteRef}
      value={busqueda}
      suggestions={sugerencias}
      completeMethod={buscarSugerencias}
      onChange={(e) => setBusqueda(e.value)}
      onSelect={handleSeleccion}
      onKeyDown={handleKeyDown}
      field="etiqueta"
      placeholder={placeholder}
      loading={cargando}
      delay={0}
      minLength={minLength}
      optionGroupLabel="etiqueta"
      optionGroupChildren="items"
      scrollHeight="400px"
      panelStyle={{ minWidth: "400px", zIndex: 1100 }}
      css={{
        "& .p-autocomplete-input": {
          borderRadius: "20px",
          width: "clamp(160px, 20vw, 300px)"
        }
      }}
      optionGroupTemplate={(group) => (
        <div
          css={{
            fontWeight: "600"
          }}
        >
          {group.etiqueta}
        </div>
      )}
      itemTemplate={(item) => <div>{item.etiqueta}</div>}
    />
  );
}
